import { useMemo, useState } from "react";
import { useGetIdentity, useListContext } from "ra-core";

import { List } from "@/components/admin/list";
import { ListPagination } from "@/components/admin/list-pagination";

import { usePageMode } from "@/hooks/usePageMode";
import { CompanyEmpty } from "./CompanyEmpty";
import { ImageList } from "./GridList";
import {
  CompanyPageHead,
  type CompanyKpiFilter,
  type CompanyViewMode,
} from "./CompanyPageHead";
import { CompanyAgentView } from "./CompanyAgentView";
import { CompanyFilterBar } from "./CompanyFilterBar";
import { CompanyTable } from "./CompanyTable";

// Sprint 8.1 (suite) — Suppression de la prop `actions={<CompanyListActions />}`
// du <List> ra-core. Les boutons SortButton + ExportButton + CreateButton sont
// désormais consolidés dans CompanyPageHead à droite du titre (conforme maquette
// ent-direction1.jsx Title row). FabrikTopBar `/companies` perd aussi son
// createTo (cf pattern Sprint 7 T7.1.d.2 fait pour /contacts).

export const CompanyList = () => {
  const { identity } = useGetIdentity();
  if (!identity) return null;
  return (
    <List
      title={false}
      perPage={50}
      sort={{ field: "created_at", order: "DESC" }}
      pagination={<ListPagination rowsPerPageOptions={[25, 50, 100, 200]} />}
      disableBreadcrumb
    >
      <CompanyListLayout />
    </List>
  );
};

const CompanyListLayout = () => {
  const { data, isPending, filterValues, total } = useListContext();
  const hasFilters = filterValues && Object.keys(filterValues).length > 0;

  const [mode, , toggleMode] = usePageMode("companies");
  const [kpiFilter, setKpiFilter] = useState<CompanyKpiFilter>("all");
  const [viewMode, setViewMode] = useState<CompanyViewMode>("list");

  // Sprint 10.x — softFilters côté front consomme :
  // - filterValues._population (filter bar Population) → "plateforme"/"prospection"
  // - filterValues._temperature (filter bar Température manuelle legacy)
  // - kpiFilter (rangée KPIs Signal + Valeur) → bascule l'un des 9 flags
  //   dans la column `kpi` consommée par CompanyTable.
  const softFilters = useMemo(
    () => ({
      population: (filterValues?._population as string) ?? undefined,
      temperature: (filterValues?._temperature as string) ?? undefined,
      kpi: kpiFilter === "all" ? undefined : kpiFilter,
    }),
    [filterValues, kpiFilter],
  );

  if (isPending) return null;
  if (!data?.length && !hasFilters && mode === "classic") {
    return <CompanyEmpty />;
  }

  const shownCount = data?.length ?? 0;
  const totalCount = total ?? 0;

  return (
    <div className="w-full flex flex-col gap-4">
      <CompanyPageHead
        mode={mode}
        onToggleMode={toggleMode}
        activeKpiFilter={kpiFilter}
        onKpiFilterChange={setKpiFilter}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      {mode === "agent" ? (
        <CompanyAgentView />
      ) : (
        <>
          <CompanyFilterBar shownCount={shownCount} totalCount={totalCount} />
          {viewMode === "list" ? (
            <CompanyTable softFilters={softFilters} />
          ) : (
            <div className="flex flex-col gap-4">
              <ImageList />
            </div>
          )}
        </>
      )}
    </div>
  );
};
